import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../../components/Toast'
import { useConfirm } from '../../components/ConfirmModal'
import { useAuth } from '../../hooks/useAuth'
import type { DashboardData, PageTab } from './types'

export interface CredentialStats {
  exposed_identities?: number
  exposed_employees?: number
  exposed_customers?: number
  exposed_with_password?: number
  new_30d?: number
  total?: number
}

// What the People tab should open with when another tab links to it
export interface PeopleFilter {
  sourceType?: string
  status?: string
}

/**
 * Page-level state for Dark Web (#397 step 3d). Tabs fetch their own data; this holds what
 * the header, tab counts and Overview share, and the scan/export actions.
 */
export function useDarkWebPageState() {
  const toast = useToast()
  const confirmDialog = useConfirm()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [domains, setDomains] = useState<Array<{ domain: string }>>([])
  const [credentialStats, setCredentialStats] = useState<CredentialStats | null>(null)
  const [pageTab, setPageTab] = useState<PageTab>('overview')
  const [peopleFilter, setPeopleFilter] = useState<PeopleFilter>({})
  // Bumped after a scan so tabs refetch
  const [dataVersion, setDataVersion] = useState(0)

  const canRemediate = user?.role === 'admin' || user?.role === 'contributor'

  async function loadData() {
    setLoading(true)
    setLoadError(null)
    try {
      const [dashboardRes, domainsRes, statsRes] = await Promise.all([
        api.getDarkWebDashboard({}),
        api.getDarkWebDomains(),
        api.getDarkWebCredentialStats(),
      ])
      if (dashboardRes.success) setDashboard(dashboardRes.dashboard as DashboardData)
      else setLoadError((dashboardRes as { error?: string }).error || 'The server returned an error.')
      if (domainsRes.success) setDomains(domainsRes.domains)
      if (statsRes.success) setCredentialStats(statsRes.stats)
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Network error.')
    } finally {
      setLoading(false)
    }
  }

  async function refreshStats() {
    try {
      const res = await api.getDarkWebCredentialStats()
      if (res.success) setCredentialStats(res.stats)
    } catch {
      // Counts refresh on the next page load
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  async function handleScan(force = false) {
    const confirmed = await confirmDialog({
      title: force ? 'Full rescan' : 'Check for new exposures',
      message: force
        ? 'This rescans all domains, then removes open alerts the new scan no longer finds. Resolved and false-positive alerts are kept. If any domain fails to scan, existing alerts are left untouched.'
        : 'This checks every monitored domain for new exposures. Existing results and remediation status are kept.',
      confirmText: force ? 'Run full rescan' : 'Check now',
      type: force ? 'danger' : 'warning',
    })
    if (!confirmed) return

    setScanning(true)
    try {
      const res = await api.runDarkWebScan({ force })
      if (res.success) {
        toast.success(res.message)
        await loadData()
        setDataVersion(v => v + 1)
      } else {
        toast.error(res.message || 'Scan failed')
      }
    } catch {
      toast.error('Failed to run scan')
    } finally {
      setScanning(false)
    }
  }

  async function handleExport() {
    setExporting(true)
    try {
      const blob = await api.exportDarkWebCredentials()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `dark-web-credentials-${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to export credentials')
    } finally {
      setExporting(false)
    }
  }

  // Open the People tab with a filter (used by Overview's "Needs action" items)
  function openPeople(filter: PeopleFilter = {}, category: 'employee' | 'customer' = 'employee') {
    setPeopleFilter(filter)
    setPageTab(category === 'customer' ? 'customers' : 'people')
  }

  return {
    user,
    canRemediate,
    loading,
    loadError,
    loadData,
    scanning,
    exporting,
    dashboard,
    domains,
    credentialStats,
    refreshStats,
    pageTab,
    setPageTab,
    peopleFilter,
    openPeople,
    dataVersion,
    handleScan,
    handleExport,
  }
}

export type DarkWebPageState = ReturnType<typeof useDarkWebPageState>
